import { apiClient } from "./client";

/** Một ngân hàng nhận được chuyển khoản nhanh. `bin` là mã NAPAS, thứ đi vào chuỗi QR. */
export type Bank = {
  bin: string;
  code: string;
  shortName: string;
  fullName: string;
};

export type BankAccount = {
  id: string;
  bankBin: string;
  bankShortName: string;
  bankFullName: string;
  accountNo: string;
  accountName: string;
  active: boolean;
  /** Đã có người trong phòng khám khẳng định tài khoản này là của phòng khám. */
  verified: boolean;
};

/**
 * Mã QR cho một khoản tiền. `payload` là chuỗi EMVCo do server dựng — web tự vẽ thành ảnh, không
 * tải ảnh từ dịch vụ nào bên ngoài: trên đường thanh toán, một máy chủ ảnh chết nghĩa là bệnh nhân
 * nhìn vào ô trống và không trả được tiền.
 */
export type Qr = {
  payload: string;
  bankShortName: string;
  accountNo: string;
  accountName: string;
  amountVnd: number;
  transferContent: string;
};

/**
 * Chỉ dẫn chuyển khoản ĐÃ PHÁT HÀNH cho một hoá đơn (#18998).
 *
 * Khác `Qr` ở một điểm quyết định: nó có `intentId`, tức là server đã ghi lại rằng mã này được
 * phát hành lúc đó, cho hoá đơn đó, trỏ vào tài khoản đó. Khi tiền về, máy đối chiếu với bản ghi
 * ấy chứ không dựng lại ý nghĩa của mã từ cấu hình hiện tại của phòng khám.
 */
export type PaymentIntent = {
  intentId: string;
  invoiceId: string;
  invoiceNumber: string;
  transferCode: string;
  qrPayload: string;
  bankShortName: string;
  accountNo: string;
  accountName: string;
  amountVnd: number;
};

export type SaveBankAccount = {
  bankBin: string;
  accountNo: string;
  accountName: string;
};

const BASE = "/v1/billing/bank-account";

export const clinicBankAccountApi = {
  banks: () => apiClient.get<Bank[]>(`${BASE}/banks`).then((r) => r.data),
  list: () => apiClient.get<BankAccount[]>(BASE).then((r) => r.data),
  create: (body: SaveBankAccount) => apiClient.post<string>(BASE, body).then((r) => r.data),
  update: (id: string, body: SaveBankAccount) => apiClient.put<void>(`${BASE}/${id}`, body),
  /**
   * Bật tài khoản nhận viện phí. `attested` là lời khẳng định của người bấm rằng tài khoản thuộc
   * về phòng khám — server từ chối nếu không có, và ghi lại ai khẳng định, lúc nào.
   */
  activate: (id: string, attested: boolean) =>
    apiClient.post<void>(`${BASE}/${id}/activate`, { attested }),
  disableAll: () => apiClient.post<void>(`${BASE}/disable`),
  /** Mã XEM TRƯỚC ở màn khai tài khoản. Không gắn hoá đơn, không để lại dấu vết. */
  qr: (amountVnd: number, content: string) =>
    apiClient
      .get<Qr>(`${BASE}/qr`, { params: { amountVnd, content } })
      .then((r) => r.data),
  /**
   * Phát hành mã thu tiền cho một hoá đơn. POST vì nó tạo ra một bản ghi; gọi lại nhiều lần cho
   * cùng hoá đơn vẫn trả về đúng một mã.
   */
  paymentQr: (invoiceId: string) =>
    apiClient.post<PaymentIntent>(`${BASE}/qr/invoice/${invoiceId}`).then((r) => r.data),
};

/** Một khoản tiền đã vào tài khoản nhưng chưa gắn được vào hoá đơn nào. */
export type UnmatchedTransaction = {
  id: string;
  provider: string;
  providerTxnId: string;
  gateway: string;
  accountNo: string;
  amountVnd: number;
  transferContent: string | null;
  occurredAt: string;
  reason: string | null;
};

const TXN_BASE = "/v1/billing/bank-transactions";

export const clinicBankTransactionApi = {
  unmatched: () =>
    apiClient.get<UnmatchedTransaction[]>(`${TXN_BASE}/unmatched`).then((r) => r.data),
  resolve: (id: string, note: string) =>
    apiClient.post<void>(`${TXN_BASE}/${id}/resolve`, { note }),
};

export const clinicBankTransactionQueryKeys = {
  unmatched: () => ["billing", "bank-transactions", "unmatched"] as const,
};

export const clinicBankAccountQueryKeys = {
  banks: () => ["billing", "bank-account", "banks"] as const,
  list: () => ["billing", "bank-account", "list"] as const,
  qr: (amountVnd: number, content: string) =>
    ["billing", "bank-account", "qr", amountVnd, content] as const,
  paymentQr: (invoiceId: string) =>
    ["billing", "bank-account", "payment-qr", invoiceId] as const,
};
